import { Callout, InputGroup, Intent, Icon } from "@blueprintjs/core";
import { madSDK } from "lib/sdk";
import { useSelector } from "react-redux";
import { selectIsLoggedIn } from "appReducers/authReducer";
import { LoginLogo } from "components/LoginLogo";
import SmithersCard from "components/Reusable/SmithersCard";
import Layout from "components/Reusable/Layout";
import { FormGroup } from "frontier/lib/components";
import { Button, ButtonColor, ButtonVariant } from "madhive/components";
import { Button as KitButton } from "@madhive/kit-ui";
import { RootState } from "rootReducer";
import "./Login.scss";
import { ensureUserCanLoginFromThisAddress } from "lib/utils/env";
import {
  SSO_ERRORS,
  SSO_LOOKUP_FAILED,
  tenantSSOAuthDomain
} from "lib/utils/tenantSSO";
import { useTenantSSO } from "@madhive/mad-hooks";
import { FC, MouseEvent, useState, useEffect, ChangeEventHandler } from "react";
import IconContainer from "./IconContainer";

interface UserEnteredCredentials {
  email: string;
  password: string;
}

interface Props {
  signInFn?: (email: string, password: string) => Promise<void>;
}

// eslint-disable-next-line import/prefer-default-export
export const LoginView: FC<Props> = ({ signInFn }) => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const selectInitializationError = (state: RootState) =>
    state.auth.initializationError;
  const userInitializationError = useSelector(selectInitializationError);

  const [loading, setLoading] = useState<boolean>(false);
  const [form, updateForm] = useState<UserEnteredCredentials>({
    email: "",
    password: ""
  });
  const [error, setError] = useState<string>("");
  // An IdP bookmark tile links with `/login?sso=<email domain>`.
  const ssoParam = new URLSearchParams(window.location.search).get("sso");
  const sso = useTenantSSO({
    emailOrDomain: form.email || ssoParam || "",
    verify: ensureUserCanLoginFromThisAddress,
    authDomain: tenantSSOAuthDomain()
  });
  const ssoLoading = sso.status === "processing";
  // A failed lookup only matters when the tile asked for SSO; otherwise the
  // password form still works and the error would just be noise.
  const ssoError = sso.error
    ? SSO_ERRORS[sso.error]
    : ssoParam && sso.lookupFailed && SSO_LOOKUP_FAILED;

  useEffect(() => {
    if (loading) {
      setLoading(!userInitializationError);
    }
  }, [userInitializationError]);

  if (isLoggedIn) {
    return null;
  }

  const handleLoginAttempt = async (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();

    setError("");
    setLoading(true);
    const { email, password } = form;

    const attemptLogin = async (userEmail: string, userPassword: string) => {
      if (signInFn) {
        await signInFn(userEmail, userPassword);
      } else {
        await madSDK.authentication.auth(
          userEmail,
          userPassword,
          ensureUserCanLoginFromThisAddress
        );
      }
    };

    await attemptLogin(email.trim(), password)
      .catch(() => attemptLogin(email.trim(), password.trim()))
      .catch(() => {
        setError(
          "Login failed. Please check your username and password and try again."
        );
        setLoading(false);
      });
  };

  const handleSSOAttempt = async (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setError("");
    await sso.signIn();
  };

  const updateField =
    (key: keyof UserEnteredCredentials): ChangeEventHandler<HTMLInputElement> =>
    e => {
      e.persist();

      if (!e.target) {
        return;
      }

      updateForm({
        ...form,
        [key]: e.target.value
      });
      setError("");
    };

  return (
    <Layout fillPage narrowWidth>
      <SmithersCard.Card
        sharpCorners
        className="login-box"
        data-testid="login-view"
      >
        <SmithersCard.Body className="card-body">
          <LoginLogo />

          <form>
            <fieldset
              className="fieldset"
              disabled={loading || ssoLoading}
              aria-busy={loading || ssoLoading}
              data-testid="login-fieldset"
            >
              <FormGroup
                label="Email"
                labelFor="email"
                className="input-wrapper"
              >
                <InputGroup
                  required
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={updateField("email")}
                  rightElement={
                    <IconContainer>
                      <Icon icon="envelope" />
                    </IconContainer>
                  }
                  large
                />
              </FormGroup>
              <FormGroup
                label="Password"
                labelFor="password"
                className="input-wrapper"
              >
                <InputGroup
                  required
                  id="password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={updateField("password")}
                  rightElement={
                    <IconContainer>
                      <Icon icon="lock" />
                    </IconContainer>
                  }
                  large
                />
              </FormGroup>
              <Button
                loading={loading}
                id="login-button"
                data-testid="login-button"
                variant={ButtonVariant.CONTAINED}
                type="submit"
                fill
                color={ButtonColor.PRIMARY}
                onClick={handleLoginAttempt}
              >
                Log In
              </Button>
              {sso.provider ? (
                <KitButton
                  name="sso-login-button"
                  data-testid="sso-login-button"
                  className="sso-button"
                  variant="outline"
                  actionType="button"
                  isLoading={ssoLoading}
                  onClick={handleSSOAttempt}
                >
                  Sign in with SSO
                </KitButton>
              ) : null}
              {error || ssoError ? (
                <Callout
                  intent={Intent.DANGER}
                  data-testid="error-text"
                  className="success-callout"
                >
                  {error || ssoError}
                </Callout>
              ) : null}
            </fieldset>
          </form>
        </SmithersCard.Body>
      </SmithersCard.Card>
    </Layout>
  );
};
